import React from "react";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { cva, type VariantProps } from "class-variance-authority";
import { RnCheckmark, RnDash } from "@remi/icons/next";

import { cn } from "../utils";

const checkboxVariants = cva(
  "relative inline-flex items-center rounded-[4px] border border-border-dark bg-transparent text-background-primary transition-[background-color,border-color] focus-within:ring-2 focus-within:ring-border-tertiary disabled:cursor-not-allowed data-[state=checked]:border-button-bgPrimary data-[state=checked]:bg-button-bgPrimary",
  {
    variants: {
      size: { sm: "h-3 min-h-3 w-3 min-w-3", md: "h-4 min-h-4 w-4 min-w-4", lg: "h-5 min-h-5 w-5 min-w-5" },
    },
    defaultVariants: {
      size: "md",
    },
  },
);

export interface RemiCheckboxProps
  extends React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>,
    VariantProps<typeof checkboxVariants> {
  indeterminate?: boolean;
}

const RemiCheckbox = ({
  ref,
  className,
  indeterminate,
  size = "md",
  ...props
}: RemiCheckboxProps & { ref?: React.Ref<React.ComponentRef<typeof CheckboxPrimitive.Root>> }) => (
  <CheckboxPrimitive.Root
    ref={ref}
    className={cn(checkboxVariants({ size }), "motion-reduce:transition-none", className)}
    {...props}
  >
    <CheckboxPrimitive.Indicator forceMount className={cn("group items-full flex w-full items-center justify-center")}>
      {indeterminate !== true && (
        <RnCheckmark
          size={size === "lg" ? "sm" : "xs"}
          className={cn(
            "[clip-path:inset(0_100%_0_0)]",
            "group-data-[state=checked]:[clip-path:inset(0_0_0_0)]",
            "group-data-[state=checked]:motion-safe:transition-[clip-path]",
            "group-data-[state=checked]:motion-safe:duration-200",
            "group-data-[state=checked]:motion-safe:ease-[cubic-bezier(0.22,1,0.36,1)]",
          )}
        />
      )}
      {indeterminate === true && <RnDash size={size === "lg" ? "sm" : "xs"} />}
    </CheckboxPrimitive.Indicator>
  </CheckboxPrimitive.Root>
);
RemiCheckbox.displayName = "RemiCheckbox";

export { RemiCheckbox };
